// src/lib/strengthScale.ts
// ============================================================
// THE STRENGTH LABEL SCALE — ONE DEFINITION, APP-WIDE
//
//   >=95 Excellent · >=80 Strong · >=60 Moderate · >=40 Developing · >=20 Weak · else Minimal
//
// ONE CONSUMER LEFT: the Organization Profile page's strength sidebar. This scale was
// promoted out of the EA personalization config so that shared surface could use it without
// a cross-boundary import. The EA metric that shared it has since been removed, and Profile
// Strength is a DIFFERENT metric that survives on its own terms, so the scale stays.
//
// WHY THIS EXISTS: the Organization Profile page used to run its own scale
// (>=90 Excellent / >=75 Good / >=50 Fair / >=25 Needs Attention / else Needs Work). The same
// state therefore read "Complete · 100%" on the /home card and "64% Fair" on the profile page —
// with "Fair" not even existing in the home widget's vocabulary. That second scale is retired.
//
// Consumers that paint with Tailwind classes use `color`/`bgColor`/`barColor`; consumers that
// paint with inline hex (the profile sidebar's SVG ring) use STRENGTH_HEX keyed on the label,
// so the THRESHOLDS AND LABELS still have exactly one source.
// ============================================================

export interface StrengthLevel {
  label: string;
  color: string;
  bgColor: string;
  barColor: string;
}

export function getStrengthLevel(pct: number): StrengthLevel {
  if (pct >= 95) return {
    label: 'Excellent',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    barColor: 'bg-emerald-500',
  };
  if (pct >= 80) return {
    label: 'Strong',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    barColor: 'bg-green-500',
  };
  if (pct >= 60) return {
    label: 'Moderate',
    color: 'text-[#18365E]',
    bgColor: 'bg-[#18365E]/10',
    barColor: 'bg-[#18365E]',
  };
  if (pct >= 40) return {
    label: 'Developing',
    color: 'text-[#FF6E4C]',
    bgColor: 'bg-[#FF6E4C]/10',
    barColor: 'bg-[#FF6E4C]',
  };
  if (pct >= 20) return {
    label: 'Weak',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    barColor: 'bg-amber-500',
  };
  return {
    label: 'Minimal',
    color: 'text-slate-500',
    bgColor: 'bg-slate-100',
    barColor: 'bg-slate-400',
  };
}

/** Hex equivalents for consumers that paint inline (SVG rings, style attributes). */
export const STRENGTH_HEX: Record<string, string> = {
  Excellent: '#10B981',
  Strong: '#10B981',
  Moderate: '#18365E',
  Developing: '#FF6E4C',
  Weak: '#F59E0B',
  Minimal: '#717182',
};

/** The hex paint for a score, resolved through the one scale. */
export function getStrengthHex(pct: number): string {
  return STRENGTH_HEX[getStrengthLevel(pct).label] ?? '#717182';
}
